import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/format";
import { MfaChallenge } from "@/components/MfaChallenge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft, BookOpen } from "lucide-react";

export default function LoginAdmin() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [pass, setPass] = useState("");
    const [busy, setBusy] = useState(false);
    const [challenge, setChallenge] = useState(null);

    const doAdmin = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await login(email, pass);
            if (res.mfa) setChallenge(res);
            else { toast.success("Welcome back"); navigate("/app"); }
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Brand panel */}
            <div className="hidden lg:flex flex-col justify-between bg-emerald-50 text-brand-emerald p-12 relative overflow-hidden">
                <div className="relative z-10">
                    <img src="/procureed-logo.png" alt="ProcureEd" className="h-12 w-auto" />
                </div>
                <div className="relative z-10 max-w-md">
                    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 border border-emerald-200 text-brand-emerald px-3 py-1 text-xs font-medium mb-5">
                        <ShieldCheck className="h-3.5 w-3.5" /> Platform Operations
                    </div>
                    <h2 className="text-3xl font-semibold tracking-tight leading-tight">Platform Administration</h2>
                    <p className="mt-4 text-slate-600">
                        Manage school tenants, subscriptions and releases across ProcureEd.
                    </p>
                </div>
                <a href="/api/docs" target="_blank" rel="noreferrer" className="relative z-10 inline-flex items-center gap-2 text-sm text-brand-teal hover:text-brand-emerald transition-colors" data-testid="admin-product-guide-link">
                    <BookOpen className="h-4 w-4" /> Read the Product Guide
                </a>
                <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-brand-teal/10 blur-3xl" />
            </div>

            {/* Form */}
            <div className="flex flex-col justify-center px-6 sm:px-12 py-10 bg-brand-bone">
                <div className="w-full max-w-md mx-auto">
                    <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-emerald mb-8" data-testid="back-home-link">
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                    <div className="lg:hidden mb-6">
                        <img src="/procureed-logo.png" alt="ProcureEd" className="h-11 w-auto" />
                    </div>
                    <h1 className="text-2xl font-semibold tracking-tight text-brand-emerald flex items-center gap-2">
                        <ShieldCheck className="h-6 w-6 text-brand-teal" /> Platform Admin
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Sign in with your platform administrator credentials.</p>

                    {challenge ? (
                        <div className="mt-6">
                            <MfaChallenge challenge={challenge} onComplete={() => { toast.success("Signed in"); navigate("/app"); }} />
                        </div>
                    ) : (
                    <form onSubmit={doAdmin} className="space-y-4 mt-6 bg-white border border-border rounded-xl p-6">
                        <div>
                            <Label htmlFor="a-email">Admin email</Label>
                            <Input id="a-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="admin-email-input" required />
                        </div>
                        <div>
                            <Label htmlFor="a-pass">Password</Label>
                            <Input id="a-pass" type="password" value={pass} onChange={(e) => setPass(e.target.value)} className="mt-1.5" data-testid="admin-password-input" required />
                        </div>
                        <Button type="submit" disabled={busy} className="w-full bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="admin-login-button">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                        </Button>
                    </form>
                    )}

                    <div className="mt-6 flex items-center justify-between text-sm">
                        <Link to="/login" className="text-muted-foreground hover:text-brand-emerald" data-testid="school-login-link">School sign in →</Link>
                        <a href="/api/docs" target="_blank" rel="noreferrer" className="lg:hidden inline-flex items-center gap-1 text-brand-teal hover:underline" data-testid="admin-product-guide-link-mobile">
                            <BookOpen className="h-4 w-4" /> Product Guide
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
